// CMO note — Commercial value: Makes our own marketing routes legible to search and AI answer engines with per-page JSON-LD — dogfooding the AEO outcome we sell.
import { useEffect } from 'react';

/* =================================================================
 * usePageSeo — lightweight per-route SEO for the Seogence SPA.
 *
 * The app ships a single static index.html with one global <title>
 * and description. Public marketing routes need their own titles,
 * descriptions, canonical URLs, social cards, and structured data so
 * search engines and AI tools describe each page accurately.
 *
 * This hook updates the document head on mount and restores the
 * previous values on unmount, so navigating between routes leaves a
 * clean head behind. It also injects JSON-LD structured data, which
 * is on-brand for a product whose whole job is structured data.
 * ================================================================= */

export const SITE_NAME = 'Seogence';

// Canonical production origin. Used unconditionally for canonical, og:url,
// and JSON-LD URLs — these state where content canonically lives, which is
// a constant, not a function of how the visitor arrived. Must match the
// host in the web/public/sitemap*.xml family (the index and its static
// children) and the robots.txt Sitemap directive.
const CANONICAL_ORIGIN = 'https://seogence.com';

/**
 * Sitewide social card, used whenever a route does not supply its own image.
 * Defaulted here rather than per route: an omitted image is invisible in
 * review — the page looks fine and only the shared link is bare — so every
 * route opting in by default is safer than 51 chances to forget.
 * Generated at 1200x630, the Open Graph reference size.
 */
const DEFAULT_OG_IMAGE = '/og.png';

export interface PageSeo {
  /** Full document title. A " — Seogence" suffix is not added automatically. */
  title: string;
  /** Meta description (~150-160 chars for best results). */
  description?: string;
  /** Canonical path beginning with "/" (e.g. "/for/ecommerce"). */
  path?: string;
  /** Open Graph / Twitter image URL (absolute or root-relative). */
  image?: string;
  /** Open Graph type. Defaults to "website". */
  type?: string;
  /** JSON-LD structured data objects injected for this page. */
  jsonLd?: Array<Record<string, unknown>>;
}

function siteOrigin(): string {
  return CANONICAL_ORIGIN;
}

function absoluteUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) return value;
  return `${siteOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
}

/** Find or create a head element matching the given attributes. */
function upsertElement(
  tag: 'meta' | 'link',
  identify: Record<string, string>
): { el: Element; created: boolean } {
  const selector = `${tag}${Object.entries(identify)
    .map(([key, val]) => `[${key}="${val}"]`)
    .join('')}`;
  const existing = document.head.querySelector(selector);
  if (existing) {
    return { el: existing, created: false };
  }
  const el = document.createElement(tag);
  Object.entries(identify).forEach(([key, val]) => el.setAttribute(key, val));
  document.head.appendChild(el);
  return { el, created: true };
}

export function usePageSeo(seo: PageSeo): void {
  const { title, description, path, image, type = 'website', jsonLd } = seo;

  useEffect(() => {
    const canonicalUrl = path ? absoluteUrl(path) : siteOrigin() + window.location.pathname;
    const imageUrl = absoluteUrl(image ?? DEFAULT_OG_IMAGE);

    // Track what we changed so the previous head is restored on unmount.
    const restores: Array<() => void> = [];

    const previousTitle = document.title;
    document.title = title;
    restores.push(() => {
      document.title = previousTitle;
    });

    const setMeta = (identify: Record<string, string>, content: string) => {
      const { el, created } = upsertElement('meta', identify);
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      restores.push(() => {
        if (created) {
          el.remove();
        } else if (prev !== null) {
          el.setAttribute('content', prev);
        }
      });
    };

    const setLink = (identify: Record<string, string>, href: string) => {
      const { el, created } = upsertElement('link', identify);
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      restores.push(() => {
        if (created) {
          el.remove();
        } else if (prev !== null) {
          el.setAttribute('href', prev);
        }
      });
    };

    if (description) setMeta({ name: 'description' }, description);
    if (canonicalUrl) {
      setLink({ rel: 'canonical' }, canonicalUrl);
    }

    // Open Graph
    setMeta({ property: 'og:site_name' }, SITE_NAME);
    setMeta({ property: 'og:title' }, title);
    if (description) setMeta({ property: 'og:description' }, description);
    setMeta({ property: 'og:type' }, type);
    if (canonicalUrl) setMeta({ property: 'og:url' }, canonicalUrl);
    if (imageUrl) setMeta({ property: 'og:image' }, imageUrl);

    // Twitter
    setMeta({ name: 'twitter:card' }, imageUrl ? 'summary_large_image' : 'summary');
    setMeta({ name: 'twitter:title' }, title);
    if (description) setMeta({ name: 'twitter:description' }, description);
    if (imageUrl) setMeta({ name: 'twitter:image' }, imageUrl);

    // JSON-LD structured data.
    // Remove any pre-existing static nodes (emitted by prerender-meta.js for
    // non-JS crawlers) so the page never carries duplicate WebPage graphs.
    document.head.querySelectorAll('script[data-seogence-seo="true"]').forEach((el) => el.remove());

    const scripts: HTMLScriptElement[] = [];
    if (jsonLd && jsonLd.length > 0) {
      jsonLd.forEach((node) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.dataset.seogenceSeo = 'true';
        script.textContent = JSON.stringify(node);
        document.head.appendChild(script);
        scripts.push(script);
      });
    }

    return () => {
      // Restore in reverse so original values come back cleanly.
      for (let i = restores.length - 1; i >= 0; i -= 1) {
        restores[i]();
      }
      scripts.forEach((script) => script.remove());
    };
  }, [title, description, path, image, type, jsonLd]);
}

/** Build a JSON-LD FAQPage node from question/answer pairs. */
export function faqJsonLd(faqs: Array<{ q: string; a: string }>): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

/**
 * Build a JSON-LD BreadcrumbList node from label/path pairs.
 *
 * Home is prepended here rather than at each call site. A trail that starts
 * mid-hierarchy describes less of the site than one rooted at the homepage,
 * and doing it structurally means a new page cannot forget it or spell it
 * differently — three separate conventions had already drifted apart before
 * this was centralised. Mirrored by buildBreadcrumb in
 * scripts/prerender-meta.js, which must produce the identical trail.
 *
 * Pass only the levels below Home; callers never pass Home themselves.
 */
export function breadcrumbJsonLd(
  items: Array<{ name: string; path: string }>
): Record<string, unknown> {
  const trail = [{ name: 'Home', path: '/' }, ...items];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: trail.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

/** Build a JSON-LD WebPage node for a marketing route. */
export function webPageJsonLd(seo: {
  name: string;
  description: string;
  path: string;
  /** Set on explanatory routes that also emit a techArticleJsonLd node. */
  hasArticle?: boolean;
}): Record<string, unknown> {
  const node: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.name,
    description: seo.description,
    url: absoluteUrl(seo.path),
    isPartOf: { '@id': `${CANONICAL_ORIGIN}/#website` },
  };
  if (seo.hasArticle) {
    node.mainEntity = { '@id': `${absoluteUrl(seo.path)}#article` };
  }
  return node;
}

/**
 * Build a JSON-LD TechArticle node for an explanatory route.
 *
 * These pages are the only routes eligible for an Article rich result, and
 * they are the ones AI answer engines quote. This must stay in step with the
 * TechArticle branch in scripts/prerender-meta.js: usePageSeo strips the
 * prerendered [data-seogence-seo] nodes on mount, so a route that omits this
 * silently loses the markup the moment React hydrates.
 *
 * Dates are the route file's real git history — first commit and last commit.
 * Never approximate them; a wrong date is a false fact served as structured data.
 */
export function techArticleJsonLd(article: {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified: string;
}): Record<string, unknown> {
  const url = absoluteUrl(article.path);
  return {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    '@id': `${url}#article`,
    headline: article.headline,
    description: article.description,
    url,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    // Google lists image as recommended for Article; without one the result is
    // eligible but visually far weaker.
    image: absoluteUrl(DEFAULT_OG_IMAGE),
    inLanguage: 'en',
    isPartOf: { '@id': `${CANONICAL_ORIGIN}/#website` },
    about: { '@id': `${CANONICAL_ORIGIN}/#software` },
    // author is the named human, publisher the company — the standard Article
    // split, and a real person is the stronger authorship signal.
    author: { '@id': `${CANONICAL_ORIGIN}/#founder` },
    publisher: { '@id': `${CANONICAL_ORIGIN}/#organization` },
    mainEntityOfPage: url,
  };
}

/** Build a JSON-LD DefinedTermSet node from term/definition pairs. */
export function definedTermSetJsonLd(
  terms: Array<{ term: string; definition: string }>
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'Seogence Glossary',
    hasDefinedTerm: terms.map((t) => ({
      '@type': 'DefinedTerm',
      name: t.term,
      description: t.definition,
    })),
  };
}
